// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — the DOM screens: menu, help, about, the camera notice, and the two results
// panels. Pure HTML builders (no state), wired up by main.ts.

import { escapeHtml, fmtScore } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

function modeChips(modeId: string): string {
  return MODE_IDS.map(
    (id) =>
      `<button class="chip${id === modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === modeId}">${escapeHtml(modeOf(id).name)}</button>`,
  ).join('');
}

export function menuHtml(o: { modeId: string; best: number; muted: boolean; dateStr: string }): string {
  const mode = modeOf(o.modeId);
  const best = o.best > 0 ? `Best: ${fmtScore(o.best)}` : 'No score yet';
  return `
    <section class="screen menu">
      <div class="brand">
        <div class="logo" aria-hidden="true"></div>
        <h1>Waft</h1>
        <p class="tagline">Wave your hand to catch the fireflies.</p>
      </div>
      <div class="modepick">
        <div class="chips" role="group" aria-label="Choose a mode">${modeChips(o.modeId)}</div>
        <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
        <p class="mode-best">${best}</p>
      </div>
      <div class="menu-actions">
        <button class="btn primary" id="playRandom">Play</button>
        <button class="btn" id="playDaily">Daily swarm</button>
        <button class="btn" id="playFriends">Play with friends</button>
      </div>
      <div class="menu-links">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <h2>How to play</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <ul class="help-list">
      <li><strong>Fireflies</strong> drift into the squares and glow, then fade. <strong>Wave your hand</strong>
        in front of the camera, over a firefly's square, to catch it.</li>
      <li>Quick catches build a <strong>combo</strong> — the higher the combo, the more each catch is worth.
        Keep it clean: letting a firefly fade, or waving over an empty square, resets the combo.</li>
      <li><strong>Sting:</strong> wasps hide among the fireflies. Wave over a wasp and you're stung
        (points lost, combo broken) — so watch where you swing.</li>
      <li><strong>No camera?</strong> Just <strong>tap</strong> the fireflies. On a computer, click them or
        press the number keys.</li>
    </ul>
    <p class="help-foot">Race a friend on the very same swarm — everyone gets the identical fireflies.</p>
    <button class="btn primary sheet-close">Got it</button>`;
}

export function cameraNoticeHtml(): string {
  return `
    <h2>Use your camera?</h2>
    <p>Waft senses the <strong>motion of your hand</strong> to catch fireflies. The camera image stays on
      your device — <strong>nothing is recorded, stored, or sent anywhere.</strong> Only a rough measure of
      where you moved is used, and it never leaves your browser.</p>
    <div class="menu-actions">
      <button class="btn primary" id="camYes">Use camera</button>
      <button class="btn" id="camNo">Use taps instead</button>
    </div>
    <p class="help-foot">You can switch to taps any time from the menu.</p>`;
}

export function aboutHtml(): string {
  return `
    <h2>About Waft</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <p>Catch drifting fireflies by waving your hand in front of the camera. Every swarm is built from a
      seed, so a friend on the same link gets the identical fireflies — race them for the top score.</p>
    <p><strong>Your camera:</strong> Waft turns your camera into a motion controller — it measures how your
      hand moves between frames. <strong>Nothing is recorded, stored, or sent</strong>; frames are compared
      and thrown away in your browser, and the camera stops the moment you leave a game.</p>
    <p><strong>Multiplayer</strong> is peer-to-peer over WebRTC — there's no game server. Connecting to a
      friend uses a public signaling relay only to introduce the two browsers; no game data (and never any
      camera image) is put on the wire or stored on a server.</p>
    <p>No accounts, no cookies, no tracking. ${ATTRIB}</p>
    <button class="btn primary sheet-close">Close</button>`;
}

function medal(rank: number): string {
  return ['🥇', '🥈', '🥉'][rank] ?? `${rank + 1}.`;
}

/** Solo results: the score, best, catches, and a share line. */
export function soloResultsHtml(o: {
  score: number;
  catches: number;
  flies: number;
  bestCombo: number;
  mode: Mode;
  best: number;
  daily: boolean;
  isNewBest: boolean;
}): string {
  const sub = o.isNewBest ? 'New best score!' : o.best > 0 ? `Best: ${fmtScore(o.best)}` : '';
  return `
    <section class="screen results">
      <p class="res-mode">${escapeHtml(o.mode.name)}${o.daily ? ' · Daily' : ''}</p>
      <h2 class="res-time win">${fmtScore(o.score)}</h2>
      <p class="res-sub">${escapeHtml(sub)}</p>
      <p class="res-detail">Caught ${o.catches} of ${o.flies} fireflies · best combo ×${o.bestCombo}</p>
    </section>`;
}

/** Multiplayer results: EVERY player's outcome (principle #9), plus the match tally. */
export function multiResultsHtml(
  ranked: PeerResult[],
  selfSeat: number | null,
  mode: Mode,
  tally: number[],
): string {
  const rows = ranked
    .map((r, i) => {
      const me = r.seat === selfSeat;
      const detail = r.left ? 'left' : `${fmtScore(r.score)} · ${r.catches}/${r.flies} · ×${r.bestCombo}`;
      return `<li class="res-row${me ? ' me' : ''}${r.left ? ' gone' : ''}">
        <span class="res-rank">${!r.left ? medal(i) : '·'}</span>
        <span class="res-name">${escapeHtml(r.name)}${me ? ' <em>(you)</em>' : ''}</span>
        <span class="res-out">${escapeHtml(detail)}</span>
        <span class="res-wins">${tally[r.seat] ? '★'.repeat(Math.min(tally[r.seat], 5)) : ''}</span>
      </li>`;
    })
    .join('');
  return `
    <p class="res-mode">${escapeHtml(mode.name)} · most fireflies caught wins</p>
    <ul class="res-list">${rows}</ul>`;
}
